import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Modal, Spin } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import api from '../../services/api';
import styles from '../../services/styles';
import { displayMessage } from '../../helpers/messages';
import GeneralContext from '../../context/GeneralContext/GeneralContext';
import Button from '../../components/buttons/Button';
import Input from '../../components/input/Input';
import { showErrorMessage, showSuccessMessage } from '../../services/messages';

const { COLORS } = styles;

export const LoginForm = () => {
  const {
    setLoggedUser,
    mobile_mode
  } = useContext(GeneralContext);

  const [errorLogin, setErrorLogin] = useState(null);
  const [isModalRecuperarSenhaVisible, setIsModalRecuperarSenhaVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [showLoading, setShowLoading] = useState(false);

  const navigate = useNavigate();

  const onFinish = async (values) => {
    try {
      const rs = await api.auth.authenticate(values);
      const { user, token } = rs?.data;

      const formattedToken = 'Bearer ' + token;

      await api.auth.setAuthorizationToken(formattedToken);

      setLoggedUser(user);

      if (user?.must_change_password) {
        navigate('/change-password');
      }
    } catch (e) {
      showErrorMessage(e?.response?.data?.error || displayMessage('ERROR_LOGIN'));
      setErrorLogin(e?.response?.data?.error || displayMessage('ERROR_LOGIN'));
    }
  };

  const handleOkPasswordRecovery = () => {
    if (!email) {
      showErrorMessage(displayMessage('ERROR_INVALID_EMAIL'));
      return;
    }

    setShowLoading(true);

    api.auth.forgotPassword(email)
      .then(rs => {
        setTimeout(() => {
          handleCloseModalRecuperarSenha();
          setShowLoading(false);

          if (rs.status === 200) {
            showSuccessMessage('E-mail de recuperação enviado com sucesso!');
          }
        }, 500);
      })
      .catch(() => {
        setTimeout(() => {
          setShowLoading(false);
          showErrorMessage('Erro ao enviar e-mail de recuperação');
        }, 500);
      });
  };

  const handleCloseModalRecuperarSenha = () => {
    if (showLoading) {
      return;
    }

    setIsModalRecuperarSenhaVisible(false);
    setEmail('');
  };

  const renderModalRecuperarSenha = () => {
    return (
      <>
        <Modal
          title={<div style={{ color: COLORS.PRIMARY }}>{displayMessage('FORGOT_PASSWORD')}</div>}
          open={isModalRecuperarSenhaVisible}
          onCancel={handleCloseModalRecuperarSenha}
          onOk={handleOkPasswordRecovery}
          centered={true}
          footer={
            !showLoading && (
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Button onClick={handleCloseModalRecuperarSenha}>
                  {displayMessage('CANCEL')}
                </Button>
                <Button type="primary" onClick={handleOkPasswordRecovery}>
                  {displayMessage('CONFIRM')}
                </Button>
              </div>
            )
          }
        >
          <div style={{ 
            display: 'flex', 
            flexDirection: 'row', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: showLoading ? 114 : 70 
          }}>
            {showLoading ? (
              <div>
                <Spin tip="Aguarde..." />
              </div>
            ) : (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                fontSize: 13, 
                flex: 1, 
                color: COLORS.PRIMARY 
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: 5 }}>
                  Digite seu e-mail cadastrado:
                </div>
                <Input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder={displayMessage('EMAIL')}
                />
              </div>
            )}
          </div>
        </Modal>
      </>
    );
  };

  return (
    <>
      {isModalRecuperarSenhaVisible && renderModalRecuperarSenha()}

      <>
        <div
          style={{
            fontWeight: 'bold',
            marginBottom: 15,
            fontSize: mobile_mode ? 20 : 24,
            textShadow: '#777 1px 2px 1px, 0px 2px 1px rgb(0 0 0 / 0%)',
            color: COLORS.SECONDARY,
          }}
        >
          Login
        </div>

        <Form
          name="normal_login"
          onFinish={onFinish}
          style={{ maxWidth: 400, width: '100%' }}
          layout='vertical'
          requiredMark={false}
        >
          <div style={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
            <div
              style={{
                color: COLORS.GRAY[50],
                textShadow: '#777 1px 2px 1px, 0px 2px 1px rgb(0 0 0 / 0%)',
                fontWeight: 'bold',
                display: 'flex'
              }}>
              {displayMessage('EMAIL')}
            </div>

            <Form.Item
              name="email"
              rules={[{ required: true, message: 'Por favor, informe o e-mail!' }]}
              style={{ marginBottom: 15 }}
            >
              <Input
                prefix={<UserOutlined className="site-form-item-icon" />}
                placeholder="Digite seu e-mail"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
            </Form.Item>
          </div>

          <div style={{ display: 'flex', gap: 2, flexDirection: 'column', marginTop: 5 }}>
            <div
              style={{
                color: COLORS.GRAY[50],
                textShadow: '#777 1px 2px 1px, 0px 2px 1px rgb(0 0 0 / 0%)',
                fontWeight: 'bold',
                display: 'flex'
              }}>
              {displayMessage('PASSWORD')}
            </div>

            <Form.Item
              name="password"
              rules={[{ required: true, message: 'Por favor, informe a senha!' }]}
              style={{ marginBottom: 25 }}
            >
              <Input
                prefix={<LockOutlined className="site-form-item-icon" />}
                type="password"
                placeholder="Digite sua senha"
              />
            </Form.Item>
          </div>

          <Form.Item hasFeedback style={{ marginBottom: 10, marginTop: 5 }}>
            <Button
              type="primary"
              style={{
                width: "100%",
                fontWeight: 'bold',
                textShadow: '0px 0px 5px #666',
              }}
              htmlType="submit"
            >
              {displayMessage('LOGIN')}
            </Button>
          </Form.Item>

          <Form.Item style={{ margin: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                style={{
                  color: COLORS.SECONDARY,
                  fontWeight: 'bold',
                  textShadow: '#777 1px 2px 1px, 0px 2px 1px rgb(0 0 0 / 0%)',
                }}
                type="link"
                onClick={() => { setIsModalRecuperarSenhaVisible(true); setEmail('') }}
              >
                {displayMessage('FORGOT_PASSWORD')}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </>
    </>
  );
};



